export enum AssetPathKey {
    ACQUISITION_DATE = "acquisition_date",
    ACTIVATION_LOCK_ENABLED = "activation_lock_enabled",
    ASSET_ID = "asset_id",
    PARENT_ASSET_ID = "parent_asset_id",
    ASSET_STATE = "asset_state",
    ASSET_TAG = "asset_tag",
    ASSET_TYPE = "asset_type",
    ASSIGNED_TO = "assigned_to",
    AZURE_AD_DEVICE_ID = "azure_ad_device_id",
    BATTERY_CYCLE_COUNT = "battery_cycle_count",
    BATTERY_DESIGNED_CAPACITY = "battery_designed_capacity",
    BATTERY_FULL_CHARGE_CAPACITY = "battery_full_charge_capacity",
    BATTERY_HEALTH = "battery_health",
    BATTERY_MANUFACTURE_DATE = "battery_manufacture_date",
    BLANCCO_DURATION = "blancco_duration",
    BLANCCO_ERASABILITY = "blancco_erasability",
    BLANCCO_GRADE = "blancco_grade",
    CHASSIS_TYPE = "chassis_type",
    COMPLIANCE = "compliance",
    CONTRACT = "contract",
    COST = "cost",
    CPU_CLOCK_SPEED = "cpu_clock_speed",
    CPU_CORE_COUNT = "cpu_core_count",
    CPU_MODEL = "cpu_model",
    CREATED_AT = "created_at",
    CREATED_BY = "created_by",
    CURRENCY = "currency",
    DEFENDER_ONBOARDING_STATUS = "defender_onboarding_status",
    DEFENDER_SENSOR_HEALTH = "defender_sensor_health",
    DEPARTMENT = "department",
    DEPLOYMENT_PROFILE = "deployment_profile",
    DEPRECIATION = "depreciation",
    DEVICE_NAME = "device_name",
    DEVICE_OWNERSHIP = "device_ownership",
    DEVICE_TYPE = "device_type",
    DISCOVERY_SOURCE = "discovery_source",
    DISK_SPACE = "disk_space",
    DISK_TYPE = "disk_type",
    DISPOSAL_DATE = "disposal_date",
    DISPOSITION_GROUP = "disposition_group",
    DOMAIN = "domain",
    ENCLOSURE_SERIAL = "enclosure_serial",
    ENROLLMENT_DATE_TIME = "enrollment_date_time",
    AUTOPILOT_ENROLLMENT_STATE = "autopilot_enrollment_state",
    ENROLLMENT_TYPE = "enrollment_type",
    EOL = "eol",
    EOS = "eos",
    ETHERNET_MAC_ADDRESS = "ethernet_mac_address",
    FIND_MY_IPHONE = "find_my_iphone",
    FIRST_DISCOVERED = "first_discovered",
    FQDN = "fqdn",
    FREE_SPACE = "free_space",
    FREE_STORAGE_SPACE = "free_storage_space",
    GROUP_TAG = "group_tag",
    HOST_NAME = "host_name",
    INSTALL_STATUS = "install_status",
    INVENTORY_ID = "inventory_id",
    IP_ADDRESS = "ip_address",
    LAST_AUDIT_DATE = "last_audit_date",
    LAST_CONTACT = "last_contact",
    LAST_ERASURE_DATE = "last_erasure_date",
    LAST_ERASURE_STATE = "last_erasure_state",
    LEASE = "lease",
    LIFECYCLE_STAGE_STATUS = "lifecycle_stage_status",
    LOCATION = "location",
    LSA_CONFIGURATION_STATE = "lsa_configuration_state",
    MAC_ADDRESS = "mac_address",
    MANUFACTURER = "manufacturer",
    MANUFACTURER_ID = "manufacturer_id",
    MDE_DEVICE_ID = "mde_device_id",
    MEMORY_GB = "memory_gb",
    MODEL = "model",
    MODEL_NUMBER = "model_number",
    NAME = "name",
    OPERATING_SYSTEM = "operating_system",
    OS_BUILD = "os_build",
    OS_DISK_TYPE = "os_disk_type",
    PUBLIC_IP = "public_ip",
    PURCHASE_ORDER_ID = "purchase_order_id",
    REGION = "region",
    REGULATORY_REQUIREMENTS = "regulatory_requirements",
    RISK_LEVEL = "risk_level",
    SERIAL_NUMBER = "serial_number",
    STORAGE_RAM = "storage_ram",
    SYSTEM_ARCHITECTURE = "system_architecture",
    SYSTEM_SERIAL_NUMBER = "system_serial_number",
    TOTAL_RAM = "total_ram",
    TOTAL_STORAGE_SPACE_IN_BYTES = "total_storage_space_in_bytes",
    UEFI_SETTINGS_NAME = "uefi_settings_name",
    UPDATED_AT = "updated_at",
    UPDATED_BY = "updated_by",
    USAGE_TYPE = "usage_type",
    UUID = "uuid",
    VULNERABILITY_FLAGS = "vulnerability_flags",
    WARRANTY_EXPIRY_DATE = "warranty_expiry_date",
    WARRANTY_PROVIDER = "warranty_provider",
    WARRANTY_STATUS = "warranty_status",
    WORKGROUP = "workgroup",
}

function assetPathKeyToLabel(key: AssetPathKey): string {
    const joined = key.split("_").join(" ");
    return joined.charAt(0).toUpperCase() + joined.slice(1);
}

export const ALL_COLUMNS = Object.values(AssetPathKey).map((key) => ({
    value: key,
    label: assetPathKeyToLabel(key),
}));

// Maps any known source field name (from Intune, Freshservice, or ServiceNow) to its canonical asset path key.
export const SOURCE_FIELD_TO_ASSET_PATH: Record<string, AssetPathKey> = {
    acquired: AssetPathKey.ACQUISITION_DATE,
    acquisition_date: AssetPathKey.ACQUISITION_DATE,
    activationLockEnabled: AssetPathKey.ACTIVATION_LOCK_ENABLED,
    asset_id: AssetPathKey.ASSET_ID,
    parent_asset_id: AssetPathKey.PARENT_ASSET_ID,
    asset_state: AssetPathKey.ASSET_STATE,
    asset_tag: AssetPathKey.ASSET_TAG,
    asset_type: AssetPathKey.ASSET_TYPE,
    autopilot_enrollment_state: AssetPathKey.AUTOPILOT_ENROLLMENT_STATE,
    assigned_to: AssetPathKey.ASSIGNED_TO,
    autopilot_enrollment_status: AssetPathKey.AUTOPILOT_ENROLLMENT_STATE,
    azure_ad_device_id: AssetPathKey.AZURE_AD_DEVICE_ID,
    azureADDeviceId: AssetPathKey.AZURE_AD_DEVICE_ID,
    battery_cycle_count: AssetPathKey.BATTERY_CYCLE_COUNT,
    battery_designed_capacity: AssetPathKey.BATTERY_DESIGNED_CAPACITY,
    battery_full_charge_capacity: AssetPathKey.BATTERY_FULL_CHARGE_CAPACITY,
    battery_health: AssetPathKey.BATTERY_HEALTH,
    battery_manufacture_date: AssetPathKey.BATTERY_MANUFACTURE_DATE,
    blancco_duration: AssetPathKey.BLANCCO_DURATION,
    blancco_erasability: AssetPathKey.BLANCCO_ERASABILITY,
    blancco_grade: AssetPathKey.BLANCCO_GRADE,
    chassis_type: AssetPathKey.CHASSIS_TYPE,
    class: AssetPathKey.ASSET_TYPE,
    compliance: AssetPathKey.COMPLIANCE,
    complianceState: AssetPathKey.COMPLIANCE,
    contract: AssetPathKey.CONTRACT,
    cost: AssetPathKey.COST,
    cpu_clock_speed: AssetPathKey.CPU_CLOCK_SPEED,
    cpu_core_count: AssetPathKey.CPU_CORE_COUNT,
    cpu_model: AssetPathKey.CPU_MODEL,
    cpu_speed: AssetPathKey.CPU_CLOCK_SPEED,
    created_at: AssetPathKey.CREATED_AT,
    created_by: AssetPathKey.CREATED_BY,
    currency: AssetPathKey.CURRENCY,
    defenderOnboardingStatus: AssetPathKey.DEFENDER_ONBOARDING_STATUS,
    defenderSensorHealth: AssetPathKey.DEFENDER_SENSOR_HEALTH,
    department: AssetPathKey.DEPARTMENT,
    deploymentProfile: AssetPathKey.DEPLOYMENT_PROFILE,
    depreciation: AssetPathKey.DEPRECIATION,
    deviceName: AssetPathKey.DEVICE_NAME,
    deviceOwnership: AssetPathKey.DEVICE_OWNERSHIP,
    deviceType: AssetPathKey.ASSET_TYPE,
    discovery_source: AssetPathKey.DISCOVERY_SOURCE,
    disk_space: AssetPathKey.DISK_SPACE,
    disk_type: AssetPathKey.DISK_TYPE,
    disposal_date: AssetPathKey.DISPOSAL_DATE,
    disposition_group: AssetPathKey.DISPOSITION_GROUP,
    domain: AssetPathKey.DOMAIN,
    enclosure_serial: AssetPathKey.ENCLOSURE_SERIAL,
    enrollment_date: AssetPathKey.ENROLLMENT_DATE_TIME,
    enrollment_date_time: AssetPathKey.ENROLLMENT_DATE_TIME,
    enrollmentDateTime: AssetPathKey.ENROLLMENT_DATE_TIME,
    enrollmentState: AssetPathKey.AUTOPILOT_ENROLLMENT_STATE,
    enrollmentType: AssetPathKey.ENROLLMENT_TYPE,
    eol: AssetPathKey.EOL,
    eos: AssetPathKey.EOS,
    ethernetMacAddress: AssetPathKey.MAC_ADDRESS,
    find_my_iphone: AssetPathKey.FIND_MY_IPHONE,
    first_discovered: AssetPathKey.FIRST_DISCOVERED,
    fqdn: AssetPathKey.FQDN,
    free_space: AssetPathKey.FREE_SPACE,
    free_storage_space: AssetPathKey.FREE_STORAGE_SPACE,
    freeStorageSpaceInBytes: AssetPathKey.FREE_STORAGE_SPACE,
    groupTag: AssetPathKey.GROUP_TAG,
    host_name: AssetPathKey.HOST_NAME,
    id: AssetPathKey.ASSET_ID,
    install_status: AssetPathKey.INSTALL_STATUS,
    inventory_id: AssetPathKey.INVENTORY_ID,
    ip_address: AssetPathKey.IP_ADDRESS,
    ipAddress: AssetPathKey.IP_ADDRESS,
    last_audit_date: AssetPathKey.LAST_AUDIT_DATE,
    last_contact: AssetPathKey.LAST_CONTACT,
    last_discovered: AssetPathKey.LAST_CONTACT,
    last_erasure_date: AssetPathKey.LAST_ERASURE_DATE,
    last_erasure_state: AssetPathKey.LAST_ERASURE_STATE,
    last_check_in_date_time: AssetPathKey.LAST_CONTACT,
    lastCheckInDateTime: AssetPathKey.LAST_CONTACT,
    lease: AssetPathKey.LEASE,
    lifecycle_stage_status: AssetPathKey.LIFECYCLE_STAGE_STATUS,
    location: AssetPathKey.LOCATION,
    lsaConfigurationState: AssetPathKey.LSA_CONFIGURATION_STATE,
    mac_address: AssetPathKey.MAC_ADDRESS,
    managedDeviceId: AssetPathKey.ASSET_ID,
    manufacturer: AssetPathKey.MANUFACTURER,
    manufacturerID: AssetPathKey.MANUFACTURER_ID,
    mdeDeviceId: AssetPathKey.MDE_DEVICE_ID,
    memory_gb: AssetPathKey.MEMORY_GB,
    model: AssetPathKey.MODEL,
    model_category: AssetPathKey.ASSET_TYPE,
    model_number: AssetPathKey.MODEL_NUMBER,
    name: AssetPathKey.NAME,
    operatingSystem: AssetPathKey.OPERATING_SYSTEM,
    os: AssetPathKey.OPERATING_SYSTEM,
    os_build: AssetPathKey.OS_BUILD,
    os_disk_type: AssetPathKey.OS_DISK_TYPE,
    os_version: AssetPathKey.OPERATING_SYSTEM,
    osVersion: AssetPathKey.OPERATING_SYSTEM,
    owner: AssetPathKey.ASSIGNED_TO,
    ownership: AssetPathKey.DEVICE_OWNERSHIP,
    physicalMemoryInBytes: AssetPathKey.TOTAL_RAM,
    po_number: AssetPathKey.PURCHASE_ORDER_ID,
    public_ip: AssetPathKey.PUBLIC_IP,
    purchase_order_id: AssetPathKey.PURCHASE_ORDER_ID,
    ram: AssetPathKey.TOTAL_RAM,
    region: AssetPathKey.REGION,
    regulatory_requirements: AssetPathKey.REGULATORY_REQUIREMENTS,
    risk_level: AssetPathKey.RISK_LEVEL,
    serial_number: AssetPathKey.SERIAL_NUMBER,
    serialNumber: AssetPathKey.SERIAL_NUMBER,
    state: AssetPathKey.ASSET_STATE,
    storage_ram: AssetPathKey.STORAGE_RAM,
    sys_created_by: AssetPathKey.CREATED_BY,
    sys_created_on: AssetPathKey.CREATED_AT,
    sys_id: AssetPathKey.ASSET_ID,
    sys_updated_by: AssetPathKey.UPDATED_BY,
    sys_updated_on: AssetPathKey.UPDATED_AT,
    system_architecture: AssetPathKey.SYSTEM_ARCHITECTURE,
    system_serial_number: AssetPathKey.SYSTEM_SERIAL_NUMBER,
    total_ram: AssetPathKey.TOTAL_RAM,
    totalStorageSpaceInBytes: AssetPathKey.TOTAL_STORAGE_SPACE_IN_BYTES,
    uefiSettingsName: AssetPathKey.UEFI_SETTINGS_NAME,
    updated_at: AssetPathKey.UPDATED_AT,
    updated_by: AssetPathKey.UPDATED_BY,
    usage_type: AssetPathKey.USAGE_TYPE,
    userDisplayName: AssetPathKey.ASSIGNED_TO,
    userPrincipalName: AssetPathKey.ASSIGNED_TO,
    uuid: AssetPathKey.UUID,
    vulnerability_flags: AssetPathKey.VULNERABILITY_FLAGS,
    warranty_expiration: AssetPathKey.WARRANTY_EXPIRY_DATE,
    warranty_expiry_date: AssetPathKey.WARRANTY_EXPIRY_DATE,
    warranty_provider: AssetPathKey.WARRANTY_PROVIDER,
    warranty_status: AssetPathKey.WARRANTY_STATUS,
    workgroup: AssetPathKey.WORKGROUP,
};

export const MANDATORY_ASSET_FIELDS = [AssetPathKey.ASSET_ID, AssetPathKey.ASSET_TYPE, AssetPathKey.SERIAL_NUMBER];

export const DEFAULT_ASSET_OVERVIEW_FIELDS = [
    ...MANDATORY_ASSET_FIELDS,
    AssetPathKey.ASSET_STATE,
    AssetPathKey.ASSET_TAG,
    AssetPathKey.ASSIGNED_TO,
    AssetPathKey.DEPARTMENT,
    AssetPathKey.LOCATION,
    AssetPathKey.MANUFACTURER,
    AssetPathKey.REGION,
    AssetPathKey.MODEL,
];

const normalizeSourceFieldName = (fieldName: string): string => fieldName.trim().toLowerCase().replace(/\s+/g, "_");

const NORMALIZED_SOURCE_FIELD_TO_ASSET_PATH: Record<string, AssetPathKey> = Object.fromEntries(
    Object.entries(SOURCE_FIELD_TO_ASSET_PATH).map(([sourceField, assetPathKey]) => [
        normalizeSourceFieldName(sourceField),
        assetPathKey,
    ])
);

const ASSET_PATH_KEY_VALUES = new Set<string>(Object.values(AssetPathKey));

// Tolerates case and whitespace/underscore differences (e.g. "acquisition date" vs
// "acquisition_date") so equivalent field names resolve to the same asset path key.
// Also resolves any field name that is already a canonical AssetPathKey value.
export const resolveAssetPathKey = (fieldName: string): AssetPathKey | undefined => {
    if (ASSET_PATH_KEY_VALUES.has(fieldName)) {
        return fieldName as AssetPathKey;
    }
    const normalized = normalizeSourceFieldName(fieldName);
    if (ASSET_PATH_KEY_VALUES.has(normalized)) {
        return normalized as AssetPathKey;
    }
    return SOURCE_FIELD_TO_ASSET_PATH[fieldName] ?? NORMALIZED_SOURCE_FIELD_TO_ASSET_PATH[normalized];
};
